import type * as THREE from 'three'
import { GSplatSogsData } from '@/plugins/gsplat/core/GSplatSogsData'
import { GSplatSogsResource } from '@/plugins/gsplat/loaders/GSplatSogsResource'
import { SOGBundleLoader } from '@/plugins/gsplat/loaders/SOGBundleLoader'
import { SogsParser } from '@/plugins/gsplat/loaders/SogsParser'
import {
  emitGSplatLoadMilestone,
  getGSplatTelemetryNow,
  measureGSplatLoadStage,
  measureGSplatLoadStageSync,
} from '@/plugins/gsplat/telemetry/GSplatLoadTelemetry'
import { DEFAULT_MAX_CONCURRENT_LOADS, DEFAULT_MAX_RETRIES } from '@/shared/constants'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { GSplatLoadTelemetryObserver } from '@/shared/types/gsplatTelemetry'

const log = getLoggerManager().getLogger('gsplat:lod')

/** 从 sog 资源 URL 提取简短可读名，用于 perf 日志标签（如 .../0_0/meta.json → 0_0） */
function sogShortName(url: string): string {
  const clean = url.split(/[?#]/)[0]
  const parts = clean.split('/').filter(Boolean)
  const last = parts[parts.length - 1] ?? url
  if (last === 'meta.json' && parts.length >= 2) {
    return parts[parts.length - 2]
  }
  return last
}

function sogResourceType(url: string): 'sog-bundle' | 'sog-meta' {
  return url.toLowerCase().endsWith('.sog') ? 'sog-bundle' : 'sog-meta'
}

/**
 * 资源加载器抽象基类
 * 对应 PlayCanvas 的 GSplatAssetLoaderBase
 *
 * @example
 * ```typescript
 * // 自定义加载器实现
 * class MyLoader extends GSplatAssetLoaderBase {
 *   load(url: string) {
 *     // 实现异步加载逻辑
 *   }
 *   unload(url: string) {
 *     // 实现卸载逻辑
 *   }
 *   getResource(url: string) {
 *     // 返回已加载的资源
 *   }
 * }
 * ```
 */
export abstract class GSplatAssetLoaderBase {
  /**
   * 启动资源加载（fire-and-forget 模式）
   * @param url - 资源 URL
   */
  abstract load(url: string): void

  /**
   * 卸载已加载的资源
   * @param url - 资源 URL
   */
  abstract unload(url: string): void

  /**
   * 获取已加载的资源
   * @param url - 资源 URL
   * @returns 资源实例，未加载返回 null
   */
  abstract getResource(url: string): GSplatSogsResource | null

  /**
   * 查询资源是否已耗尽重试并进入最终失败状态。
   *
   * 自定义加载器未覆盖时默认没有可观测的失败终态。
   */
  isResourceFailed(_url: string): boolean {
    return false
  }

  /**
   * 销毁加载器并清理所有资源
   */
  destroy(): void {
    // 基类默认实现为空，子类应覆盖
  }
}

/**
 * 资源状态
 */
enum ResourceState {
  /** 排队等待加载 */
  Queued = 'queued',
  /** 正在加载 */
  Loading = 'loading',
  /** 加载完成 */
  Loaded = 'loaded',
  /** 加载失败 */
  Failed = 'failed',
}

/**
 * 资源条目
 */
interface ResourceEntry {
  url: string
  state: ResourceState
  resource: GSplatSogsResource | null
  parser: SogsParser | null
  promise: Promise<GSplatSogsResource> | null
  retryCount: number
  attemptCount: number
  activeAttempt: LoadAttempt | null
}

/**
 * 单次底层加载尝试。
 *
 * URL 不能作为 attempt 身份：同一 URL 在 unload 后可能重新进入队列，旧 Promise 仍可能稍后
 * settle。独立对象身份用于保证旧 attempt 无法提交或释放新 attempt 的并发槽。
 */
interface LoadAttempt {
  id: number
  number: number
  entry: ResourceEntry
  controller: AbortController
  contextLost: boolean
}

export interface GSplatAssetLoaderOptions {
  maxConcurrentLoads?: number
  maxRetries?: number
  debug?: boolean
  withCredentials?: boolean
  /** 是否加载高阶球谐数据 SHN，默认 true；实例创建后保持不变 */
  loadSHN?: boolean
  /** 单次模型加载的只读遥测 observer */
  telemetry?: GSplatLoadTelemetryObserver
  onResourceLoaded?: (url: string, resource: GSplatSogsResource) => void
  onResourceGpuReady?: (url: string, resource: GSplatSogsResource) => void
  onResourceFailed?: (url: string, error: Error) => void
}

/**
 * GSplat 资源加载器（具体实现）
 *
 * 核心功能：
 * 1. 异步加载 SOGS 文件
 * 2. 并发控制（限制同时加载数量）
 * 3. 资源缓存和复用
 * 4. 加载队列管理
 * 5. 失败重试机制
 *
 * 参考 PlayCanvas gsplat-asset-loader.js
 *
 * @example
 * ```typescript
 * // 创建加载器
 * const loader = new GSplatAssetLoader(renderer, {
 *   maxConcurrentLoads: 4,  // 最多同时加载 4 个文件
 *   maxRetries: 2,          // 失败后重试 2 次
 *   debug: true             // 启用调试日志
 * });
 *
 * // 启动加载（fire-and-forget）
 * loader.load('https://example.com/chunk-0/meta.json');
 * loader.load('https://example.com/chunk-1/meta.json');
 *
 * // 稍后检查资源是否加载完成
 * const resource = loader.getResource('https://example.com/chunk-0/meta.json');
 * if (resource) {
 *   // 资源已加载，可以使用
 *   console.log('Loaded:', resource.numSplats, 'splats');
 * }
 *
 * // 卸载不再需要的资源（减少引用计数）
 * loader.unload('https://example.com/chunk-0/meta.json');
 *
 * // 销毁加载器
 * loader.destroy();
 * ```
 */
export class GSplatAssetLoader extends GSplatAssetLoaderBase {
  /** Three.js 渲染器（用于创建 SOGS 资源） */
  private renderer: THREE.WebGLRenderer

  /** 资源映射表（URL -> 资源条目） */
  private resources = new Map<string, ResourceEntry>()

  /** 加载队列 */
  private loadQueue: ResourceEntry[] = []

  /** 当前尚未真正 settle 的底层 attempt 集合 */
  private currentlyLoading = new Set<LoadAttempt>()

  /** attempt 单调编号，仅用于诊断和确保每次尝试身份唯一 */
  private nextAttemptId = 1

  /** 最大并发加载数 */
  maxConcurrentLoads: number

  /** 最大重试次数 */
  maxRetries: number

  /** 是否已销毁 */
  private destroyed = false

  /** 是否启用调试日志 */
  private debug: boolean

  /** 是否携带跨域 Cookie */
  private withCredentials: boolean

  /** 此 LOD 模型固定使用的高阶球谐加载策略 */
  private readonly loadSHN: boolean

  /** 此 LOD 模型专属 observer；destroy 后待活动 attempt settle 再释放引用。 */
  private telemetry?: GSplatLoadTelemetryObserver

  /** 用于在宿主释放 observer 时立即断开 loader 对回调闭包的引用。 */
  private telemetryAbortHandler?: () => void

  /** context loss 期间暂停新 attempt；活动 attempt 在恢复后无损重排且不消耗网络重试次数。 */
  private contextLost = false

  private readonly onContextLost = (event: Event): void => {
    if (this.destroyed) {
      return
    }
    event.preventDefault()
    this.contextLost = true
    for (const attempt of this.currentlyLoading) {
      attempt.contextLost = true
      attempt.controller.abort()
    }
  }

  private readonly onContextRestored = (): void => {
    if (this.destroyed) {
      return
    }
    this.contextLost = false
    this.processQueue()
  }

  /** 首次加载批次起始时间戳；perf 关闭时恒为 null，保证零开销 */
  private initialBatchStart: number | null = null

  /** 首次队列清空是否已上报，确保初始可见集只统计一次 */
  private initialBatchReported = false

  /** 首次加载批次内成功加载的 sog 数量 */
  private initialBatchLoaded = 0

  /** 首次加载批次内最终失败的 sog 数量 */
  private initialBatchFailed = 0

  /** telemetry 独立的首次加载批次起点，不依赖 logging.perf 开关。 */
  private initialTelemetryBatchStart: number | null = null

  /**
   * 构造函数
   * @param renderer - Three.js 渲染器
   * @param options - 配置选项
   */
  constructor(renderer: THREE.WebGLRenderer, options?: GSplatAssetLoaderOptions) {
    super()
    this.renderer = renderer
    this.maxConcurrentLoads = options?.maxConcurrentLoads ?? DEFAULT_MAX_CONCURRENT_LOADS
    this.maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES
    this.debug = options?.debug ?? false
    this.withCredentials = options?.withCredentials ?? false
    this.loadSHN = options?.loadSHN ?? true
    this.bindTelemetry(options?.telemetry)
    this.onResourceLoaded = options?.onResourceLoaded
    this.onResourceGpuReady = options?.onResourceGpuReady
    this.onResourceFailed = options?.onResourceFailed
    renderer.domElement?.addEventListener?.('webglcontextlost', this.onContextLost)
    renderer.domElement?.addEventListener?.('webglcontextrestored', this.onContextRestored)
  }

  private readonly onResourceLoaded?: (url: string, resource: GSplatSogsResource) => void
  private readonly onResourceGpuReady?: (url: string, resource: GSplatSogsResource) => void
  private readonly onResourceFailed?: (url: string, error: Error) => void

  private notifyResourceLoaded(url: string, resource: GSplatSogsResource): void {
    try {
      this.onResourceLoaded?.(url, resource)
    } catch (error) {
      log.error(
        `[GSplatAssetLoader] onResourceLoaded callback failed for ${url}: ` +
          (error instanceof Error ? error.message : String(error))
      )
    }
  }

  private notifyResourceFailed(url: string, error: Error): void {
    try {
      this.onResourceFailed?.(url, error)
    } catch (callbackError) {
      log.error(
        `[GSplatAssetLoader] onResourceFailed callback failed for ${url}: ` +
          (callbackError instanceof Error ? callbackError.message : String(callbackError))
      )
    }
  }

  private notifyResourceGpuReady(url: string, resource: GSplatSogsResource): void {
    try {
      this.onResourceGpuReady?.(url, resource)
    } catch (error) {
      log.error(
        `[GSplatAssetLoader] onResourceGpuReady callback failed for ${url}: ` +
          (error instanceof Error ? error.message : String(error))
      )
    }
  }

  private assertLoadActive(entry: ResourceEntry, attempt: LoadAttempt): void {
    if (!this.isAttemptCurrent(entry, attempt)) {
      throw new DOMException(`Load cancelled: ${entry.url}`, 'AbortError')
    }
  }

  private isEntryCurrent(entry: ResourceEntry): boolean {
    return !this.destroyed && this.resources.get(entry.url) === entry
  }

  private isAttemptCurrent(entry: ResourceEntry, attempt: LoadAttempt): boolean {
    return (
      this.isEntryCurrent(entry) &&
      entry.activeAttempt === attempt &&
      !attempt.controller.signal.aborted
    )
  }

  private isAttemptOwned(entry: ResourceEntry, attempt: LoadAttempt): boolean {
    return this.isEntryCurrent(entry) && entry.activeAttempt === attempt
  }

  private hasLoadingAttemptForUrl(url: string): boolean {
    for (const attempt of this.currentlyLoading) {
      if (attempt.entry.url === url) {
        return true
      }
    }
    return false
  }

  private finalizeSogsData(
    entry: ResourceEntry,
    attempt: LoadAttempt,
    name: string,
    gsplatData: GSplatSogsData
  ): Promise<GSplatSogsResource> {
    return (async () => {
      try {
        this.assertLoadActive(entry, attempt)
        await log.perf.span(`sog.finalize ${name}`, async () => {
          await measureGSplatLoadStage(
            this.telemetry,
            {
              resourceType: sogResourceType(entry.url),
              resourceIdentity: entry.url,
              resourceUrl: entry.url,
              attempt: attempt.number,
              stage: 'center-generation',
            },
            async () => {
              gsplatData.prepareCodebook()
              await gsplatData.generateCenters(this.renderer, attempt.controller.signal)
            }
          )
        })
        this.assertLoadActive(entry, attempt)
      } catch (error) {
        gsplatData.destroy()
        throw error
      }

      if (this.debug) {
        log.debug(`[GSplatAssetLoader] prepared SOG resource with raw textures: ${name}`)
      }

      const endResource = log.perf.time(`sog.resource ${name}`)
      try {
        const resource = measureGSplatLoadStageSync(
          this.telemetry,
          {
            resourceType: sogResourceType(entry.url),
            resourceIdentity: entry.url,
            resourceUrl: entry.url,
            attempt: attempt.number,
            stage: 'resource-create',
          },
          () => new GSplatSogsResource(this.renderer, gsplatData)
        )
        resource.setGpuReadyCallback(() => {
          if (this.isEntryCurrent(entry) && entry.resource === resource) {
            this.notifyResourceGpuReady(entry.url, resource)
          }
        })
        resource.setGpuRestoreFailedCallback((error) => {
          if (!this.isEntryCurrent(entry) || entry.resource !== resource) {
            return
          }

          // context restore 的压缩源解码或重传失败时，旧资源仍可能被退休中的 WorldState
          // 引用。先让它延迟销毁，再把同一逻辑 entry 无损重排；新 fetch 的网络重试
          // 仍使用原有 maxRetries 规则。
          entry.resource = null
          entry.state = ResourceState.Queued
          entry.retryCount = 0
          resource.retireWhenUnused()
          log.warn(
            `[GSplatAssetLoader] GPU restore failed, reloading ${entry.url}: ${error.message}`
          )
          this.enqueue(entry, true)
        })
        return resource
      } catch (error) {
        // GSplatSogsResource 构造会接管数据所有权；构造失败时对象不会返回，必须在这里
        // 回收已解码/上传的纹理，避免重试继续叠加 GPU 与 WebContent 内存。
        gsplatData.destroy()
        throw error
      } finally {
        endResource()
      }
    })()
  }

  /**
   * 启动资源加载（fire-and-forget 模式）
   * @param url - 资源 URL
   */
  load(url: string): void {
    if (this.destroyed) {
      log.warn(`[GSplatAssetLoader] 加载器已销毁，忽略加载请求: ${url}`)
      return
    }

    // 已加载、排队中、正在加载或已失败，跳过。
    const entry = this.resources.get(url)
    if (entry) {
      return
    }

    const nextEntry: ResourceEntry = {
      url,
      state: ResourceState.Queued,
      resource: null,
      parser: null,
      promise: null,
      retryCount: 0,
      attemptCount: 0,
      activeAttempt: null,
    }
    this.resources.set(url, nextEntry)

    // 记录首次加载批次起点：仅在 perf 开启且尚未上报、尚未计时时打点（perf 关闭时零开销）
    if (this.initialBatchStart === null && !this.initialBatchReported) {
      const mgr = getLoggerManager()
      if (mgr.isPerfEnabled()) {
        this.initialBatchStart = mgr.now()
        this.initialBatchLoaded = 0
      }
    }
    if (
      this.initialTelemetryBatchStart === null &&
      !this.initialBatchReported &&
      this.telemetry &&
      !this.telemetry.signal?.aborted
    ) {
      this.initialTelemetryBatchStart = getGSplatTelemetryNow()
      this.initialBatchLoaded = 0
      this.initialBatchFailed = 0
    }

    this.enqueue(nextEntry)
  }

  /**
   * 将当前逻辑资源条目加入调度；同 URL 的旧 attempt 未 settle 时保持排队。
   */
  private enqueue(entry: ResourceEntry, prioritize = false): void {
    if (!this.isEntryCurrent(entry)) {
      return
    }

    entry.state = ResourceState.Queued
    if (!this.loadQueue.includes(entry)) {
      if (prioritize) {
        this.loadQueue.unshift(entry)
      } else {
        this.loadQueue.push(entry)
      }
    }
    this.processQueue()
  }

  /**
   * 立即开始加载资源
   * @param url - 资源 URL（支持 .sog 文件或 meta.json 目录格式）
   */
  private async startLoading(entry: ResourceEntry): Promise<void> {
    if (!this.isEntryCurrent(entry) || entry.activeAttempt) {
      return
    }

    const { url } = entry
    const attempt: LoadAttempt = {
      id: this.nextAttemptId++,
      number: ++entry.attemptCount,
      entry,
      controller: new AbortController(),
      contextLost: false,
    }
    entry.state = ResourceState.Loading
    entry.activeAttempt = attempt
    this.currentlyLoading.add(attempt)

    // 检测文件类型并选择加载方式
    const isSogBundle = url.toLowerCase().endsWith('.sog')
    const name = sogShortName(url)

    let promise: Promise<GSplatSogsResource>

    if (isSogBundle) {
      // 使用 PlayCanvas 风格的 GPU 纹理路径加载 .sog，避免把嵌入的 WebP 转成 blob URL。
      const bundleLoader = new SOGBundleLoader({
        keepCompressed: true,
        withCredentials: this.withCredentials,
        signal: attempt.controller.signal,
      })
      promise = log.perf
        .span(`sog.fetch ${name}`, () =>
          bundleLoader.loadGpuData(url, {
            renderer: this.renderer,
            preuploadTextures: true,
            retainTextureRestoreSource: true,
            signal: attempt.controller.signal,
            loadSHN: this.loadSHN,
            telemetry: this.telemetry,
            telemetryAttempt: attempt.number,
          })
        )
        .then((gsplatData) => {
          // Bundle loader 已逐纹理预上传并立即关闭解码背板；这里核验所有压缩恢复源
          // 都已接管，并在进入串行 centers 队列前标记 context-restore 状态。
          gsplatData.releaseSourceImages()
          return this.finalizeSogsData(entry, attempt, name, gsplatData)
        })
    } else {
      // 使用 SogsParser.load 加载 meta.json 目录格式
      const parser = new SogsParser()
      entry.parser = parser

      promise = log.perf
        .span(`sog.fetch ${name}`, () =>
          SogsParser.load(url, {
            withCredentials: this.withCredentials,
            signal: attempt.controller.signal,
            loadSHN: this.loadSHN,
            telemetry: this.telemetry,
            telemetryAttempt: attempt.number,
          })
        )
        .then(({ textures, meta }) => {
          // 创建 GSplatSogsData
          const gsplatData = new GSplatSogsData({
            meta,
            numSplats: meta.count,
            means_l: textures.means_l,
            means_u: textures.means_u,
            quats: textures.quats,
            scales: textures.scales,
            sh0: textures.sh0,
            sh_centroids: textures.sh_centroids,
            sh_labels: textures.sh_labels,
            url,
          })

          return this.finalizeSogsData(entry, attempt, name, gsplatData)
        })
    }

    entry.promise = promise

    let retry = false
    try {
      const resource = await promise

      if (attempt.contextLost) {
        resource.destroy()
        if (this.isAttemptOwned(entry, attempt)) {
          retry = true
          entry.state = ResourceState.Queued
        }
        return
      }

      // 旧 attempt 永远不能提交到同 URL 的新 entry。
      if (!this.isAttemptCurrent(entry, attempt)) {
        resource.destroy()
        return
      }

      entry.state = ResourceState.Loaded
      entry.resource = resource
      entry.retryCount = 0
      this.notifyResourceLoaded(url, resource)

      // 计入首次加载批次；perf 与 telemetry 任一开启即可统计。
      if (
        (this.initialBatchStart !== null || this.initialTelemetryBatchStart !== null) &&
        !this.initialBatchReported
      ) {
        this.initialBatchLoaded++
      }
    } catch (error) {
      if (attempt.contextLost && this.isAttemptOwned(entry, attempt)) {
        // context loss 不是资源或网络失败；恢复前暂停，恢复后重新执行完整 attempt，
        // 且不消耗 maxRetries。
        retry = true
        entry.state = ResourceState.Queued
        return
      }

      // unload/destroy 或被同 URL 新 entry 取代后，不重试、不发失败事件。
      if (!this.isAttemptCurrent(entry, attempt)) {
        return
      }

      const retryCount = entry.retryCount

      if (retryCount < this.maxRetries) {
        // 重试
        entry.retryCount++
        log.warn(
          `[GSplatAssetLoader] 加载失败，重试 ${entry.retryCount}/${this.maxRetries}: ${url} - ` +
            (error instanceof Error ? error.message : String(error))
        )

        // 等当前底层 attempt 真正进入 finally 并释放槽位后再调度重试。
        retry = true
        entry.state = ResourceState.Queued
      } else {
        // 超过最大重试次数
        const finalError = error instanceof Error ? error : new Error(String(error))
        log.error(`[GSplatAssetLoader] 加载失败，已达最大重试次数: ${url} - ${finalError.message}`)
        entry.state = ResourceState.Failed
        if (!this.initialBatchReported) {
          this.initialBatchFailed++
        }
        this.notifyResourceFailed(url, finalError)
      }
    } finally {
      // 仅释放本 attempt 自己的槽；不能按 URL 删除，避免误伤同 URL 的新 attempt。
      this.currentlyLoading.delete(attempt)
      if (entry.activeAttempt === attempt) {
        entry.activeAttempt = null
        entry.promise = null
      }

      if (retry && this.isEntryCurrent(entry)) {
        this.enqueue(entry, true)
      }

      // 处理队列中的下一个
      this.processQueue()

      if (this.destroyed && this.currentlyLoading.size === 0) {
        this.releaseTelemetry()
      }
    }
  }

  /**
   * 处理加载队列
   */
  private processQueue(): void {
    if (this.destroyed || this.contextLost) {
      return
    }

    this.loadQueue = this.loadQueue.filter((entry) => this.isEntryCurrent(entry))

    // 尝试启动更多加载任务。队首可能被同 URL 的旧 attempt 阻塞，因此扫描首个可运行项，
    // 不让它阻塞其他 URL。
    while (this.currentlyLoading.size < this.maxConcurrentLoads && this.loadQueue.length > 0) {
      const queueIndex = this.loadQueue.findIndex(
        (entry) => this.isEntryCurrent(entry) && !this.hasLoadingAttemptForUrl(entry.url)
      )
      if (queueIndex === -1) {
        break
      }

      const [entry] = this.loadQueue.splice(queueIndex, 1)
      void this.startLoading(entry)
    }

    // 首次队列清空：in-flight 与队列同时归零，上报初始可见集加载完成的总耗时
    if (this.currentlyLoading.size === 0 && this.loadQueue.length === 0) {
      this.reportInitialBatchSettled()
    }
  }

  /**
   * 上报首次队列清空（初始可见集加载完成）的总耗时与 sog 数量，仅输出一次。
   * 之后由相机移动触发的加载批次不再统计；perf 关闭时为零开销 no-op。
   */
  private reportInitialBatchSettled(): void {
    if (
      this.initialBatchReported ||
      (this.initialBatchStart === null && this.initialTelemetryBatchStart === null)
    ) {
      return
    }

    const mgr = getLoggerManager()
    const perfElapsed = this.initialBatchStart === null ? null : mgr.now() - this.initialBatchStart
    const telemetryElapsed =
      this.initialTelemetryBatchStart === null
        ? null
        : getGSplatTelemetryNow() - this.initialTelemetryBatchStart
    this.initialBatchReported = true
    this.initialBatchStart = null
    this.initialTelemetryBatchStart = null

    if (mgr.isPerfEnabled() && perfElapsed !== null) {
      mgr.writeRecord({
        level: LogLevel.INFO,
        namespace: 'gsplat:lod:perf',
        message: `initialBatch settled: ${this.initialBatchLoaded} sogs in ${perfElapsed.toFixed(1)}ms`,
        timestamp: mgr.now(),
      })
    }
    if (telemetryElapsed !== null) {
      emitGSplatLoadMilestone(
        this.telemetry,
        'initial-load-batch-settled',
        telemetryElapsed,
        this.initialBatchLoaded,
        this.initialBatchFailed
      )
    }
  }

  /**
   * 卸载资源
   * @param url - 资源 URL
   */
  unload(url: string): void {
    const entry = this.resources.get(url)
    if (!entry) {
      return
    }

    // 取消底层任务，但保留并发槽，直至该 attempt 的 Promise 真正 settle。
    entry.activeAttempt?.controller.abort()
    this.loadQueue = this.loadQueue.filter((queuedEntry) => queuedEntry !== entry)

    if (entry?.resource) {
      entry.resource.destroy()
    }
    if (this.resources.get(url) === entry) {
      this.resources.delete(url)
    }

    this.processQueue()
  }

  /**
   * 获取已加载的资源
   * @param url - 资源 URL
   * @returns 资源实例，未加载返回 null
   */
  getResource(url: string): GSplatSogsResource | null {
    const entry = this.resources.get(url)
    if (entry && entry.state === ResourceState.Loaded && entry.resource?.gpuReady !== false) {
      return entry.resource
    }
    return null
  }

  override isResourceFailed(url: string): boolean {
    return this.resources.get(url)?.state === ResourceState.Failed
  }

  /**
   * 销毁加载器并清理所有资源
   */
  destroy(): void {
    this.destroyed = true
    this.renderer.domElement?.removeEventListener?.('webglcontextlost', this.onContextLost)
    this.renderer.domElement?.removeEventListener?.('webglcontextrestored', this.onContextRestored)

    // 清空队列
    this.loadQueue.length = 0

    // 请求所有底层任务取消。attempt 集合必须保留到各 Promise 真正 settle，不能提前释放槽位。
    for (const attempt of this.currentlyLoading) {
      attempt.controller.abort()
    }

    // 清空资源映射
    for (const entry of this.resources.values()) {
      entry.resource?.destroy()
    }

    this.resources.clear()
    if (this.currentlyLoading.size === 0) {
      this.releaseTelemetry()
    }
  }

  /** 绑定单次加载 observer；signal 仅释放遥测，不会中止任何资源 attempt。 */
  private bindTelemetry(observer: GSplatLoadTelemetryObserver | undefined): void {
    if (!observer || observer.signal?.aborted || observer.traceId.length === 0) return

    this.telemetry = observer
    const signal = observer.signal
    if (!signal) return

    const abortHandler = () => {
      if (this.telemetry === observer) {
        this.telemetry = undefined
      }
      this.telemetryAbortHandler = undefined
    }
    this.telemetryAbortHandler = abortHandler
    signal.addEventListener('abort', abortHandler, { once: true })
  }

  /** 移除 observer 及其 signal 监听，避免模型生命周期保留宿主回调闭包。 */
  private releaseTelemetry(): void {
    const observer = this.telemetry
    const abortHandler = this.telemetryAbortHandler
    if (observer?.signal && abortHandler) {
      observer.signal.removeEventListener('abort', abortHandler)
    }
    this.telemetryAbortHandler = undefined
    this.telemetry = undefined
  }

  /**
   * 获取加载器状态（调试用）。
   *
   * @returns 包含 total/loaded/loading/queued/failed 计数的快照
   */
  getStatus(): {
    total: number
    loaded: number
    loading: number
    queued: number
    failed: number
  } {
    let loaded = 0
    let loading = 0
    let queued = 0
    let failed = 0

    for (const entry of this.resources.values()) {
      switch (entry.state) {
        case ResourceState.Loaded:
          loaded++
          break
        case ResourceState.Loading:
          loading++
          break
        case ResourceState.Queued:
          queued++
          break
        case ResourceState.Failed:
          failed++
          break
      }
    }

    return {
      total: this.resources.size,
      loaded,
      loading,
      queued,
      failed,
    }
  }
}
